/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect, useRef } from "react";
import {
  FaBusinessTime,
  FaHistory,
  FaRegClock,
  FaCreditCard,
  FaPaypal,
} from "react-icons/fa";
import { BsDownload, BsXLg } from "react-icons/bs";
import { GiTeamIdea } from "react-icons/gi";
import { RiTeamFill } from "react-icons/ri";
import {
  IoCheckmarkCircleOutline,
  IoChatbubbleEllipsesSharp,
} from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import BillingTable from "./BillingTable";
import axios from "axios";

// --- STRIPE IMPORTS ---
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { openChatWindow } from "../components/ChatLaunchar";

// Initialize Stripe outside of component render to avoid recreating Stripe object
const stripePromise = loadStripe(
  "pk_test_51SXEmiGRTQTRi38gXY9BhiAyDrPcWMkHF4ibupKLi8p0dgSlYnAKw41RLHF9LTL5htmvMXrOFm3wcGs8udMywFR100WuHNH97R",
);

// --- Stripe Checkout Form Component ---
const StripeCheckoutForm = ({ plan }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [email, setEmail] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(""); // 'error' or 'success'

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) return; // Stripe.js hasn't loaded yet
    setIsProcessing(true);
    setMessage(null);

    // Convert price to cents/paise for Stripe (e.g. 85.00 -> 8500)
    const amountInCents = Math.round(parseFloat(plan.price) * 100);
    const currencyCode = plan.currency === "Rs" ? "inr" : "usd";

    try {
      // ⚠️ UPDATE THIS URL to match your actual backend URL!
      // If testing locally, it might be http://localhost:8000/... or whatever port your Node backend runs on.
      const API_URL =
        "https://api.confidanto.com/stripe-payment/create-payment-intent";

      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: amountInCents,
          currency: currencyCode,
          email: email,
          plan: plan.name,
        }),
      });

      // Catch HTML/404 responses before trying to parse JSON
      if (!res.ok) {
        const text = await res.text();
        // If it starts with an HTML tag, it means the endpoint is wrong/missing
        if (text.startsWith("<")) {
          throw new Error(
            `API Route not found. Check your backend URL: ${API_URL}`,
          );
        }
        throw new Error("Failed to initialize payment on the server.");
      }

      const data = await res.json();

      if (!data.clientSecret) {
        throw new Error(
          "Payment initialization failed. Missing client secret.",
        );
      }

      // Confirm the payment on the client
      const cardElement = elements.getElement(CardElement);
      const result = await stripe.confirmCardPayment(data.clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            email: email,
          },
        },
      });

      if (result.error) {
        setMessageType("error");
        setMessage(result.error.message);
      } else if (result.paymentIntent.status === "succeeded") {
        setMessageType("success");
        setMessage("✅ Payment Successful! Thank you for subscribing.");
      }
    } catch (err) {
      setMessageType("error");
      setMessage(err.message || "Server error occurred. Please try again.");
    }

    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col space-y-4 w-full">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Email Address
        </label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Card Details
        </label>
        <div className="p-3 border border-gray-300 rounded-lg bg-white shadow-sm">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: "16px",
                  color: "#32325d",
                  fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                  "::placeholder": { color: "#aab7c4" },
                },
                invalid: { color: "#fa755a", iconColor: "#fa755a" },
              },
            }}
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full bg-[#4142dc] text-white font-bold py-3 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center mt-2"
      >
        {isProcessing ? (
          <span className="flex items-center">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
            Processing...
          </span>
        ) : (
          `Pay ${plan.currency}${plan.price}`
        )}
      </button>

      {message && (
        <div
          className={`p-3 mt-4 rounded-md text-sm ${
            messageType === "error"
              ? "bg-red-50 text-red-700 border border-red-200"
              : "bg-green-50 text-green-700 border border-green-200"
          }`}
        >
          {message}
        </div>
      )}
    </form>
  );
};

// --- PayPal Button Component ---
const PayPalButton = ({ plan }) => {
  const paypalRef = useRef();

  const getCurrencyCode = (currencySymbol) => {
    switch (currencySymbol) {
      case "Rs":
        return "INR";
      case "$":
        return "USD";
      default:
        return "USD";
    }
  };

  const currencyCode = getCurrencyCode(plan.currency);

  useEffect(() => {
    if (window.paypal && paypalRef.current) {
      paypalRef.current.innerHTML = "";

      window.paypal
        .Buttons({
          createOrder: async () => {
            try {
              const res = await fetch(
                "https://api.confidanto.com/paypal-payment/create-order",
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    amount: plan.price.trim(),
                    currency: currencyCode,
                  }),
                },
              );
              const data = await res.json();
              if (data.id) return data.id;
              console.error("Failed to create order:", data);
              alert("Could not initiate PayPal Checkout.");
              return null;
            } catch (error) {
              console.error("Error in createOrder:", error);
              alert("An error occurred. Please try again.");
            }
          },
          onApprove: async (data) => {
            try {
              const res = await fetch(
                "https://api.confidanto.com/paypal-payment/capture-order",
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ orderID: data.orderID }),
                },
              );
              const details = await res.json();
              alert("Payment Successful! Transaction ID: " + details.id);
            } catch (error) {
              console.error("Error in onApprove:", error);
              alert("Payment could not be processed. Please contact support.");
            }
          },
          onError: (err) => {
            console.error("PayPal Checkout onError", err);
            alert("An error occurred with your payment. Please try again.");
          },
        })
        .render(paypalRef.current);
    }
  }, [plan, currencyCode]);

  return <div ref={paypalRef} className="w-full relative z-0"></div>;
};

// --- Payment Modal Component (Redesigned) ---
const PaymentModal = ({ isOpen, onClose, plan, isSdkReady }) => {
  const [paymentMethod, setPaymentMethod] = useState("card"); // 'card' or 'paypal'

  if (!isOpen || !plan) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl flex flex-col md:flex-row overflow-hidden transform transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute z-10 text-gray-400 bg-white bg-opacity-80 rounded-full p-2 top-3 right-3 hover:text-gray-800 hover:bg-gray-100 transition-colors"
        >
          <BsXLg size={20} />
        </button>

        {/* Left Side: Order Summary */}
        <div className="w-full md:w-5/12 bg-gray-50 p-8 border-r border-gray-100">
          <p className="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-2">
            Order Summary
          </p>
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            {plan.name} Plan
          </h2>

          <div className="flex items-end mb-6">
            <span className="text-4xl font-extrabold text-gray-900">
              {plan.currency}
              {plan.price}
            </span>
            <span className="text-gray-500 font-medium ml-1 pb-1">
              {plan.month.replace("/", " / ")}
            </span>
          </div>

          <div className="h-px w-full bg-gray-200 mb-6"></div>

          <h3 className="text-sm font-semibold text-gray-900 mb-4">
            What's included:
          </h3>
          <ul className="space-y-3 text-sm text-gray-600">
            {plan.features.map((feature, idx) => {
              // Extract text cleanly for the summary sidebar
              const isString = typeof feature === "string";
              const textContent = isString
                ? feature.replace("- ", "")
                : feature.text?.props?.children || "Feature Included";
              return (
                <li key={idx} className="flex items-start">
                  <IoCheckmarkCircleOutline className="text-blue-500 text-lg mr-2 mt-0.5 flex-shrink-0" />
                  <span>{textContent}</span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Right Side: Payment Methods */}
        <div className="w-full md:w-7/12 p-8 bg-white flex flex-col justify-center">
          <h3 className="text-xl font-bold text-gray-800 mb-6">
            Payment Details
          </h3>

          {/* Payment Method Tabs */}
          <div className="flex space-x-4 mb-8">
            <button
              onClick={() => setPaymentMethod("card")}
              className={`flex-1 py-3 px-4 rounded-xl flex items-center justify-center border-2 transition-all ${
                paymentMethod === "card"
                  ? "border-[#4142dc] bg-blue-50 text-[#4142dc]"
                  : "border-gray-200 hover:border-gray-300 text-gray-500"
              }`}
            >
              <FaCreditCard className="mr-2 text-lg" />
              <span className="font-semibold text-sm">Credit Card</span>
            </button>
            <button
              onClick={() => setPaymentMethod("paypal")}
              className={`flex-1 py-3 px-4 rounded-xl flex items-center justify-center border-2 transition-all ${
                paymentMethod === "paypal"
                  ? "border-[#0070ba] bg-[#f0f8ff] text-[#0070ba]"
                  : "border-gray-200 hover:border-gray-300 text-gray-500"
              }`}
            >
              <FaPaypal className="mr-2 text-lg" />
              <span className="font-semibold text-sm">PayPal</span>
            </button>
          </div>

          {/* Conditional Payment Forms */}
          <div className="min-h-[250px] flex flex-col justify-center">
            {paymentMethod === "card" ? (
              <Elements stripe={stripePromise}>
                <StripeCheckoutForm plan={plan} />
              </Elements>
            ) : (
              <div className="w-full">
                {isSdkReady ? (
                  <PayPalButton plan={plan} />
                ) : (
                  <div className="flex flex-col items-center py-10">
                    <div className="w-8 h-8 border-4 border-blue-200 border-t-[#0070ba] rounded-full animate-spin"></div>
                    <p className="mt-3 text-gray-500 text-sm">
                      Initializing PayPal...
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <p className="text-center text-xs text-gray-400 mt-6">
            Payments are secure and encrypted.
          </p>
        </div>
      </div>
    </div>
  );
};

// --- Main Billing Component ---
const Billing = () => {
  const navigate = useNavigate();
  const [userRegion, setUserRegion] = useState("Loading...");
  const [isSdkReady, setIsSdkReady] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const handleBuyClick = (plan) => {
    setSelectedPlan(plan);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPlan(null);
  };

  const [currentTrial, setCurrentTrial] = useState(""); //Free-Trial, standard
  const [remainingDays, setRemainingDays] = useState(0);

  const handleChatSupport = () => {
    // Feature: Chat now support trigger
    console.log("Chat support clicked");
    openChatWindow();
  };

  const fetchPlan = () => {
    axios
      .post(
        "https://api.confidanto.com/projects-data/subscription/project-summary",
        { email: localStorage.getItem("email") },
      )
      .then((res) => {
        if (res.data) {
          // setRemainingDays(res.data.remainingDays);
          setRemainingDays(0);
          setCurrentTrial(res.data.subscriptionType);
        }
      })
      .catch((err) => console.error("ERR: ", err));
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async (position) => {
        const { latitude, longitude } = position.coords;
        const response = await fetch(
          `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
        );
        const data = await response.json();
        if (data.countryName === "India") setUserRegion("India");
        else if (
          ["United States", "United Kingdom", "Europe"].includes(
            data.countryName,
          )
        )
          setUserRegion("USA, Europe, UK");
        else setUserRegion("Other");
      });
    } else {
      setUserRegion("Geolocation not supported");
    }

    const addPayPalScript = () => {
      if (window.paypal) {
        setIsSdkReady(true);
        return;
      }
      const script = document.createElement("script");
      script.src =
        "https://www.paypal.com/sdk/js?client-id=AUHE-CETsl9_eRmHcj_LPy7V8fTYUHboG4RsfgZouzuJJQmH2cFjTfCSulzpu3xv9k-h3i1hNprd8ydD&components=buttons";
      script.type = "text/javascript";
      script.async = true;
      script.onload = () => setIsSdkReady(true);
      document.body.appendChild(script);
    };

    addPayPalScript();
    fetchPlan();
  }, []);

  const plans = [
    {
      id: "IN-1",
      key: "Free-Trial",
      name: "Free",
      price: "0",
      month: " /month",
      currency: "Rs",
      region: "India",
      features: [
        {
          text: <b>Media Planning</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        " - Detailed Forecasting for all categories",
        " - Seed Keyword Analysis",
        " - Age/Gender Analysis",
        " - Location Analysis",
        {
          text: <b>Ad copy Crafting</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
      ],
      icon: (
        <BsDownload className="absolute top-2 right-2 text-gray-100 text-4xl" />
      ),
    },
    {
      id: "IN-2",
      key: "standard",
      name: "Standard",
      price: "800",
      month: "/project /month",
      currency: "Rs",
      region: "India",
      features: [
        {
          text: <b>Media Planning</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        "Detailed Forecasting for all categories",
        "Seed Keyword Analysis",
        "Age/Gender Analysis",
        "Location Analysis",
        {
          text: <b>Reporting</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        {
          text: <b>Time Segmentation</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        "Daily Reporting",
        "Weekly Reporting",
        "Monthly Reporting",
        {
          text: <b>Ad copy Crafting</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        {
          text: <b>Alerts/Notifications</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
      ],
      icon: (
        <FaBusinessTime className="absolute top-2 right-2 text-gray-100 text-4xl" />
      ),
    },
    {
      id: "US-1",
      key: "Free-Trial",
      name: "Free",
      price: "0",
      month: " /month",
      currency: "$",
      region: "USA, Europe, UK",
      features: [
        {
          text: <b>Media Planning</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        " - Detailed Forecasting for all categories",
        " - Seed Keyword Analysis",
        " - Age/Gender Analysis",
        " - Location Analysis",
        {
          text: <b>Ad copy Crafting</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
      ],
      icon: (
        <BsDownload className="absolute top-2 right-2 text-gray-100 text-4xl" />
      ),
    },
    {
      id: "US-2",
      key: "standard",
      name: "Standard",
      price: "85.00",
      month: " /month",
      currency: "$",
      region: "USA, Europe, UK",
      features: [
        {
          text: <b>Upto 2 projects</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        {
          text: <b>Media Planning</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        " - Detailed Forecasting for all categories",
        " - Seed Keyword Analysis",
        " - Age/Gender Analysis",
        " - Location Analysis",
        {
          text: <b>Reporting</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        " - Daily Reporting",
        " - Weekly Reporting",
        " - Monthly Reporting",
        {
          text: <b>Time Segmentation</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        {
          text: <b>Ad copy Crafting</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        {
          text: <b>Alerts/Notifications</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
      ],
      icon: (
        <FaBusinessTime className="absolute top-2 right-2 text-gray-100 text-4xl" />
      ),
    },
    {
      id: "US-3",
      key: "advance",
      name: "Advance",
      price: "135.00",
      month: " /month",
      currency: "$",
      region: "USA, Europe, UK",
      features: [
        {
          text: <b>Upto 4 projects</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        {
          text: <b>Media Planning</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        " - Detailed Forecasting for all categories",
        " - Seed Keyword Analysis",
        " - Age/Gender Analysis",
        " - Location Analysis",
        {
          text: <b>Reporting</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        " - Daily Reporting",
        " - Weekly Reporting",
        " - Monthly Reporting",
        {
          text: <b>Time Segmentation</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        {
          text: <b>Ad copy Crafting</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        {
          text: <b>Alerts/Notifications</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
      ],
      icon: (
        <GiTeamIdea className="absolute top-2 right-2 text-gray-100 text-4xl" />
      ),
    },
    {
      id: "US-4",
      key: "advance+",
      name: "Advance+",
      price: "160.00",
      month: " /month",
      currency: "$",
      region: "USA, Europe, UK",
      features: [
        {
          text: <b>Upto 6 projects</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        {
          text: <b>Media Planning</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        " - Detailed Forecasting for all categories",
        " - Seed Keyword Analysis",
        " - Age/Gender Analysis",
        " - Location Analysis",
        {
          text: <b>Reporting</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        " - Daily Reporting",
        " - Weekly Reporting",
        " - Monthly Reporting",
        {
          text: <b>Time Segmentation</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        {
          text: <b>Ad copy Crafting</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
        {
          text: <b>Alerts/Notifications</b>,
          icon: <IoIosCheckmarkCircleOutline className="text-blue-500 mr-2" />,
        },
      ],
      icon: (
        <RiTeamFill className="absolute top-2 right-2 text-gray-100 text-4xl" />
      ),
    },
  ];

  const filteredPlans =
    userRegion === "Loading..."
      ? plans.filter((plan) => plan.region === "USA, Europe, UK")
      : plans.filter((plan) => plan.region.includes(userRegion));

  const signUpDate = localStorage.getItem("start_date");
  const trialPeriodDays = 14;
  const trialEndDate = new Date(signUpDate);
  trialEndDate.setDate(trialEndDate.getDate() + trialPeriodDays);

  function calculateDaysLeftInTrial(trialEndDate) {
    const currentDate = new Date();
    const timeDiff = trialEndDate - currentDate;
    const daysLeft = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    return daysLeft;
  }
  const daysLeft = calculateDaysLeftInTrial(trialEndDate);
  const daysLeftText =
    daysLeft > 0
      ? `${daysLeft} days left in your trial`
      : "Your trial has ended";

  const billingTableRef = useRef(null);

  const [showPaymentHistoryButton, setShowPaymentHistoryButton] =
    useState(true);

  const handleScrollToHistory = () => {
    setShowPaymentHistoryButton(false);
    if (billingTableRef.current) {
      billingTableRef.current.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  return (
    <>
      <PaymentModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        plan={selectedPlan}
        isSdkReady={isSdkReady}
      />

      <div className="flex flex-col justify-end bg-[#4142dc] p-2 relative font-neue ">
        <div className="flex items-center justify-center">
          <div className="lg:w-28 w-16 border-t border-zinc-300"></div>
          <h5 className="text-zinc-100 mx-4 font-semibold text-sm lg:text-xl animate-pulse">
            {daysLeftText}
          </h5>
          <div className="lg:w-28 w-16 border-t border-zinc-300"></div>
        </div>
      </div>

      <div className="relative bg-white flex flex-col items-center bg-blend-exclusion lg:mb-20 pb-10">
        {false && showPaymentHistoryButton && (
          <a
            onClick={handleScrollToHistory}
            className="absolute top-7 right-8 z-10 flex items-center px-4 py-2 bg-white border border-gray-300 rounded-full shadow-lg hover:bg-gray-100 transition-colors cursor-pointer"
            aria-label="Go to payment history"
          >
            <FaHistory className="mr-2 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">
              Payment History
            </span>
          </a>
        )}

        <div className="relative w-full max-w-6xl flex justify-center items-center my-8 px-4">
          <h1 className="text-3xl font-bold text-center">
            Confidanto Premium plans
          </h1>
          {remainingDays > 0 ? (
            <div className="absolute left-4 top-1/2 -translate-y-1/2 hidden lg:flex items-center space-x-2 bg-green-100 text-green-800 text-sm font-semibold px-3 py-1.5 rounded-md border border-green-200 shadow-sm">
              <FaRegClock />
              <span>
                <strong>{remainingDays}</strong> day
                {remainingDays !== 1 && "s"} left
              </span>
            </div>
          ) : (
            <div className="absolute left-4 top-1/2 -translate-y-1/2 hidden lg:flex items-center space-x-2 bg-red-500 text-white text-sm font-semibold px-3 py-1.5 rounded-md border border-red-500 shadow-sm">
              <FaRegClock />
              <span>Plan Expired</span>
            </div>
          )}
        </div>

        <div className="lg:flex my-2">
          <p>All plans include:</p>
          <p
            className="lg:mx-4 flex cursor-pointer hover:text-blue-500"
            onClick={() => {
              navigate("/customer-support");
            }}
          >
            <IoCheckmarkCircleOutline className="text-blue-500 mr-2 lg:text-2xl" />{" "}
            24/7 customer care
          </p>
          <p className="flex lg:ml-2">
            <IoCheckmarkCircleOutline className="text-blue-500 mr-2 lg:text-2xl" />{" "}
            We also provide a dedicated support team
          </p>
        </div>
        <p className="flex mb-8 mx-16 pl-8">
          <IoCheckmarkCircleOutline className="text-blue-500 mr-2 text-2xl" />{" "}
          14 days free trial where you can create as many projects as you want!
        </p>

        <div
          className={`grid ${
            userRegion === "India"
              ? "grid-cols-2"
              : "grid-cols-1 md:grid-cols-2 lg:grid-cols-4"
          } gap-6 w-full max-w-max px-4`}
        >
          {filteredPlans.map((plan) => (
            <div
              key={plan.id}
              className={`relative p-6 rounded-lg shadow-lg border flex flex-col transition-all duration-300 ease-in-out
           ${
             plan.key === currentTrial
               ? "border-2 border-indigo-500 bg-indigo-50"
               : "border-gray-200 bg-white"
           }`}
            >
              {plan.key === currentTrial && (
                <div className="absolute top-0 right-4 -translate-y-1/2 bg-indigo-600 text-white text-xs font-semibold px-4 py-1 rounded-full shadow-lg">
                  Current Plan
                </div>
              )}
              <div className="flex-grow">
                {plan.icon}
                <h2 className="text-2xl font-bold mb-4">{plan.name}</h2>
                <p className="text-2xl font-semibold mb-6 p-3 bg-[#f0f2ff] rounded-sm text-center">
                  <span className="text-sm align-middle">{plan.currency}</span>
                  {plan.price}
                  <span className="text-sm">{plan.month}</span>
                </p>
                <h3 className="text-lg font-extralight text-gray-500 mb-2">
                  Plan Includes:
                </h3>
                <ul className="mb-6">
                  {plan.features.map((feature, index) => (
                    <li
                      key={index}
                      className="flex items-center text-gray-500 mb-2"
                    >
                      {feature.icon}
                      {feature.text || feature}
                    </li>
                  ))}
                </ul>
              </div>

              {plan.name !== "Free" && (
                <div className="flex justify-center mt-auto">
                  <button
                    onClick={() => handleBuyClick(plan)}
                    className="group relative min-w-fit lg:w-4/6 text-[rgb(65,66,220)] flex justify-center py-2 px-6 border border-[#4142dc] text-base font-medium rounded-md hover:text-white bg-transparent hover:bg-[rgb(65,66,220)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Buy Plan
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div ref={billingTableRef} id="payment-history-section">
        <BillingTable />
      </div>

      {/* Floating Chat Support Icon */}
      <button
        onClick={handleChatSupport}
        className="fixed bottom-8 right-8 z-50 bg-[#4142dc] text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform active:scale-95 flex items-center justify-center"
        aria-label="Chat Support"
      >
        <IoChatbubbleEllipsesSharp size={28} />
      </button>
    </>
  );
};
export default Billing;
